import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GlobalContext } from "../../../context";
import { logoutHandler } from "../../../context/actions/auth";
import Logo from "../../AllVon/VonHeader/assets/logo.png";
import NotificationBell from "../NotificationBell";
import MobileNav, { useMobile } from "./MobileNav";
import Icon from "./Icon";
import "./lms.css";

// [label, path, icon, matcher?]. Existing admin pages keep their routes.
const ADMIN = [
  ["Overview", [["Home", "/dashboard", "home", (p) => p === "/dashboard"]]],
  ["Learning", [
    ["Course plans", "/dashboard/staff/plans", "cal", (p) => /^\/dashboard\/staff\/(plans|weeks|quizzes)/.test(p)],
    ["Capstone & teams", "/dashboard/staff/capstone", "users"],
    ["Live classes (Zoom)", "/dashboard/staff/live", "video"],
    ["Student progress", "/dashboard/staff/progress", "chart"],
    ["Certificates", "/dashboard/staff/certificates", "award"],
    ["Cohorts & courses", "/dashboard/admin/cohorts", "book", (p) => p.startsWith("/dashboard/admin/cohorts") || p.startsWith("/dashboard/admin/courses")],
    ["Leaderboard", "/dashboard/staff/leaderboard", "trophy"],
  ]],
  ["People & payments", [
    ["Students", "/dashboard/admin/students", "user"],
    ["Subscriptions", "/dashboard/admin/subscriptions", "card"],
    ["Scholarships", "/dashboard/admin/scholarships", "cap"],
    ["Sponsors", "/dashboard/admin/sponsors", "heart"],
    ["Partner applications", "/dashboard/admin/partners", "users"],
  ]],
  ["Community & content", [
    ["Comments", "/dashboard/admin/comments", "chat"],
    ["Feedback", "/dashboard/admin/feedback", "star"],
    ["FAQs", "/dashboard/admin/faqs", "help"],
    ["Job board", "/dashboard/admin/jobs", "brief"],
  ]],
  ["Insights", [["Analytics", "/dashboard/admin/analytics", "line"]]],
  ["Account", [["Settings", "/dashboard/settings", "gear"]]],
];

// Order follows the approved prototype. Live classes (Zoom) joins the admin menu when it's built.
const INSTRUCTOR = [
  ["Teach", [
    ["Home", "/dashboard", "home", (p) => p === "/dashboard"],
    ["My classes", "/dashboard/staff/plans", "book", (p) => /^\/dashboard\/(staff\/(plans|weeks|quizzes)|instructor\/courses)/.test(p)],
    ["Capstone & teams", "/dashboard/staff/capstone", "users"],
    ["My students", "/dashboard/staff/progress", "chart"],
    ["Submissions", "/dashboard/instructor/submissions", "task"],
    ["Leaderboard", "/dashboard/staff/leaderboard", "trophy"],
  ]],
  ["Account", [["Settings", "/dashboard/settings", "gear"]]],
];

/**
 * Admin and instructor frame in the new LMS design: the student shell's light sidebar with the
 * staff menus. Existing admin pages render unchanged beside it while they're moved over.
 */
export default function StaffShell({ children }) {
  const { pathname } = useLocation();
  const mobile = useMobile();
  const [menu, setMenu] = useState(false);
  useEffect(() => setMenu(false), [pathname]);
  const navigate = useNavigate();
  const { authState: { data }, authDispatch } = useContext(GlobalContext);
  const user = data?.user;
  const isAdmin = !!user?.is_admin;
  const [confirmOut, setConfirmOut] = useState(false);
  const initials = `${user?.firstname?.[0] ?? ""}${user?.lastname?.[0] ?? ""}`.toUpperCase() || "E1";

  const logOut = async () => {
    await logoutHandler(authDispatch);
    navigate("/");
  };

  return (
    <div className="lms-frame">
      <div className="lms lms-side-wrap">
        <MobileNav open={menu} setOpen={setMenu} pathname={pathname} />
        <aside className={`side ${menu ? "open" : ""}`}>
          <div className="staff-top">
            <Link to="/" className="logo"><img src={Logo} alt="E1X Academy" /></Link>
            {!mobile && <NotificationBell />}
          </div>
          {(isAdmin ? ADMIN : INSTRUCTOR).map(([group, items]) => (
            <div key={group}>
              <div className="nav-group">{group}</div>
              <nav className="nav">
                {items.map(([label, to, icon, match, external, badge]) => external ? (
                  <a key={label} href={to} target="_blank" rel="noopener noreferrer"><Icon name={icon} />{label}<span className="nav-ext"><Icon name="external" size={14} /></span></a>
                ) : (
                  <Link key={label} to={to} className={(match ? match(pathname) : pathname.startsWith(to)) ? "on" : ""}>
                    <Icon name={icon} />{label}{badge && <span className="nav-badge">{badge}</span>}
                  </Link>
                ))}
              </nav>
            </div>
          ))}
          {confirmOut ? (
            <div className="logout-confirm">
              <span>Log out?</span>
              <button className="cbtn quiet" type="button" onClick={() => setConfirmOut(false)}>Cancel</button>
              <button className="cbtn danger" type="button" onClick={logOut}>Log out</button>
            </div>
          ) : (
            <div className="me">
              <span className="av">{user?.profile_img ? <img src={user.profile_img} alt="" /> : initials}</span>
              <div><strong>{user?.firstname} {user?.lastname ? `${user.lastname[0]}.` : ""}</strong><small>{isAdmin ? "Admin" : "Instructor"}</small></div>
              <button type="button" className="out" title="Log out" aria-label="Log out" onClick={() => setConfirmOut(true)}><Icon name="out" size={16} /></button>
            </div>
          )}
        </aside>
      </div>
      <main>{children}</main>
    </div>
  );
}
